import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface OptionContract {
  type?: string;
  strike?: number;
  expiry?: string;
  dte?: number;
  delta?: number;
  theta?: number;
  gamma?: number;
  vega?: number;
  iv?: number;
  bid?: number;
  ask?: number;
  mark?: number;
  volume?: number;
  open_interest?: number;
  pop?: number;
  option_score?: number;
}

export interface SpreadLeg {
  leg_type: 'short' | 'long';
  action: 'BUY' | 'SELL';
  type: string;
  strike: number;
  expiry: string;
  dte: number;
  delta?: number;
  theta?: number;
  gamma?: number;
  vega?: number;
  bid?: number;
  ask?: number;
  mark?: number;
}

export interface SpreadMetrics {
  net_credit: number;
  max_profit: number;
  max_loss: number;
  spread_width: number;
  put_width?: number;
  call_width?: number;
  breakeven?: number;
  breakeven_lower?: number;
  breakeven_upper?: number;
  return_on_risk: number;
  pop?: number;
  net_delta?: number;
  net_theta?: number;
  net_gamma?: number;
  net_vega?: number;
}

export interface IntradayCandle {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface PremiumSnapshot {
  timestamp: string;
  mark: number;
  bid: number;
  ask: number;
  delta?: number;
  theta?: number;
  underlying_price?: number;
}

export interface SentimentData {
  score: number;
  label: 'very_bullish' | 'bullish' | 'neutral' | 'bearish' | 'very_bearish';
  confidence: number;
  sources_count: number;
  timestamp?: string;
  catalyst?: string;
  headlines?: Array<{ title: string; publisher: string; time?: number }>;
}

export interface TradeSignal {
  signal_id: string;
  symbol: string;
  date_score: string;
  strategy: string;
  strategy_name?: string;
  direction?: string;
  total_score: number;
  score: number;
  raw_score?: number;          // pre-macro-penalty score
  macro_penalty?: number;      // points deducted by macro stress
  macro_regime?: string;       // worst macro factor regime at scoring time
  macro_applied?: boolean;     // false when the signal is direction-exempt
  price?: number;
  action?: string;
  option?: OptionContract;
  entry_details?: {
    action: string;
    strike?: number;
    expiration?: string;
    contract_type?: string;
  };
  entry_price?: number;
  target_price?: number;
  stop_loss?: number;
  current_iv?: number;
  ivr?: number;
  volume_ratio?: number;
  risk_reward_ratio?: number;
  confidence?: string;
  rationale?: string[];
  score_breakdown?: {
    ivr_score?: number;
    technical_score?: number;
    sentiment_score?: number;
    options_quality_score?: number;
  };
  risk_profile?: string;
  position_sizing?: string;
  exit_rules?: {
    profit_target?: number;
    stop_loss?: number;
    take_profit_pct?: number;
    stop_loss_pct?: number;
    time_exit_dte?: number;
    trailing_stop?: boolean;
    trailing_stop_pct?: number;
  };
  greeks?: {
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
  };
  current_price?: number;
  mark_price?: number;
  bid?: number;
  ask?: number;
  timestamp?: string;
  signalDate?: string; // Date of the signal (YYYY-MM-DD)
  sentiment?: SentimentData; // Real-time sentiment data
  legs?: SpreadLeg[]; // Multi-leg spread legs
  spread_metrics?: SpreadMetrics; // Spread risk/reward metrics
  intraday_candles?: IntradayCandle[]; // Underlying stock intraday price data
  premium_history?: PremiumSnapshot[]; // Option premium time series
}

export type MacroRegime = 'calm' | 'cautious' | 'stressed' | 'extreme' | 'unknown';

export interface MacroFactor {
  regime: MacroRegime;
  deviation_sigma?: number;
  penalty?: number;
  symbol?: string;
  today_close?: number;
}

export interface MacroState {
  as_of_date?: string;
  combined_penalty: number;
  combined_regime: MacroRegime;
  oil: MacroFactor;
  gold: MacroFactor;
  bonds: MacroFactor;
  rates: MacroFactor;
}

export interface TradeSignalsResponse {
  signals: TradeSignal[];
  signalsByDate?: { [date: string]: TradeSignal[] };
  count: number;
  date?: string; // deprecated, use weekStart/weekEnd
  weekStart?: string;
  weekEnd?: string;
  tier: string;
  riskTolerance?: string;
  macro?: MacroState | null;
}

export interface HistoricalSignalsOptions {
  startDate: string;  // YYYY-MM-DD
  endDate: string;    // YYYY-MM-DD
  limit?: number;
  lastKey?: string;
}

export interface HistoricalSignalsResponse {
  signals: TradeSignal[];
  signalsByWeek: { [weekKey: string]: TradeSignal[] }; // weekKey format: "YYYY-MM-DD:YYYY-MM-DD"
  count: number;
  pagination: { hasMore: boolean; lastKey?: string | null; totalFetched: number };
  dateRange: { start: string; end: string };
  tier: string;
  view: string;
}

export interface ShadowSignalsResult {
  v1: TradeSignal[];
  v2: TradeSignal[];
}

export interface DisagreementPick {
  strategy: string;
  direction: string;
  score: number;
  preset?: string | null;
}

export interface ShadowDisagreement {
  date: string;
  symbol: string;
  v1_picks: DisagreementPick[];
  v2_picks: DisagreementPick[];
  v1_directions: string[];
  v2_directions: string[];
  disagreement_type: string;
}

export interface ShadowDisagreementsResponse {
  disagreements: ShadowDisagreement[];
  count: number;
  dateRange: { start: string; end: string };
  unknownStrategies?: string[];
}

export const tradeSignalsService = {
  async getSignals(token: string): Promise<TradeSignalsResponse> {
    const response = await api.get('/trade-signals', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  /**
   * v2 (multi-strategy) signals for the current week. Returns the same shape
   * as getSignals but reads from prod-trade-signals-v2 via ?version=v2. The
   * frontend filters the resulting signals by user-selected preset
   * (conservative / balanced / aggressive).
   */
  async getV2Signals(token: string): Promise<TradeSignalsResponse> {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    // Default window: today only. v2 dedup keeps highest-score per
    // (symbol, strategy, preset) so we get all 3 presets back.
    const response = await api.get(
      `/trade-signals?view=current&startDate=${today}&endDate=${today}&version=v2`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  async getHistoricalSignals(token: string, options: HistoricalSignalsOptions): Promise<HistoricalSignalsResponse> {
    const params = new URLSearchParams({
      view: 'history',
      startDate: options.startDate,
      endDate: options.endDate,
    });
    if (options.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options.lastKey) {
      params.append('lastKey', options.lastKey);
    }

    const response = await api.get(`/trade-signals?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async getShadowSignals(token: string): Promise<ShadowSignalsResult> {
    const today = new Date().toISOString().slice(0, 10);
    return tradeSignalsService.getShadowSignalsForDate(token, today);
  },

  async getShadowSignalsForDate(token: string, date: string): Promise<ShadowSignalsResult> {
    const authHeader = { Authorization: `Bearer ${token}` };
    const [v1Resp, v2Resp] = await Promise.all([
      api.get(`/trade-signals?view=history&startDate=${date}&endDate=${date}&version=v1`, { headers: authHeader }),
      api.get(`/trade-signals?view=history&startDate=${date}&endDate=${date}&version=v2`, { headers: authHeader }),
    ]);
    const v1Data: HistoricalSignalsResponse = v1Resp.data;
    const v2Data: TradeSignalsResponse = v2Resp.data;
    return {
      v1: v1Data.signals || [],
      v2: v2Data.signals || [],
    };
  },

  async getShadowDisagreements(token: string, startDate?: string, endDate?: string): Promise<ShadowDisagreementsResponse> {
    const params = new URLSearchParams({ view: 'disagreements' });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/trade-signals?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // V1 AI risk briefings (Notches-style, per-signal)
  async getV1RiskBriefings(token: string, date?: string): Promise<{
    date: string;
    version: string;
    count: number;
    briefings: Array<{
      signal_id: string;
      candidateKey: string;
      symbol: string;
      strategy: string;
      preset?: string;
      version: string;
      briefing: string;
      riskLevel: 'low' | 'medium' | 'high' | string;
      watchFor: string;
      date: string;
      generatedAt: string;
      modelVersion: string;
    }>;
  }> {
    const params = new URLSearchParams({ version: 'v1' });
    if (date) params.append('date', date);
    const response = await api.get(`/trade-signals/v2/risk-briefings?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // TT stock paper-engine report (single Bobby-only endpoint)
  async getTtStockReport(token: string, opts?: { userId?: string; engineId?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (opts?.userId) params.append('userId', opts.userId);
    if (opts?.engineId) params.append('engineId', opts.engineId);
    const q = params.toString();
    const response = await api.get(`/admin/tt-stock-report${q ? '?' + q : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async refreshV1RiskBriefings(token: string, date?: string): Promise<{
    refreshed: boolean;
    statusCode?: number;
    totalWritten?: number;
    picks?: number;
    date?: string;
    version?: string;
    error?: string;
  }> {
    const response = await api.post(
      '/trade-signals/v2/risk-briefings/refresh',
      { version: 'v1', ...(date ? { date } : {}) },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  // V2 AI risk briefings (Notches-style, per-signal)
  async getV2RiskBriefings(token: string, date?: string): Promise<{
    date: string;
    count: number;
    briefings: Array<{
      signal_id: string;
      candidateKey: string;
      symbol: string;
      strategy: string;
      preset: string;
      briefing: string;
      riskLevel: 'low' | 'medium' | 'high' | string;
      watchFor: string;
      date: string;
      generatedAt: string;
      modelVersion: string;
    }>;
  }> {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    const q = params.toString();
    const response = await api.get(`/trade-signals/v2/risk-briefings${q ? '?' + q : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async refreshV2RiskBriefings(token: string, date?: string): Promise<{
    refreshed: boolean;
    statusCode?: number;
    totalWritten?: number;
    picks?: number;
    date?: string;
    error?: string;
  }> {
    const response = await api.post(
      '/trade-signals/v2/risk-briefings/refresh',
      date ? { date } : {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },
};
